import axios from "axios";
import { setUserAsGuest } from "./auth/service";
import { getClientSessionId } from "./common/clientSessionId";

// Create tab session id as soon as the API client loads (before first request).
getClientSessionId();

const axiosInstance = axios.create({ baseURL: process.env.REACT_APP_API_URL });

const extractTokenFromPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const direct = payload.token ?? payload.store_token ?? null;
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  return null;
};

const normalizeErrorMessage = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") return raw;
  if (typeof raw === "object") {
    if (typeof raw.message === "string" && raw.message.trim())
      return raw.message.trim();
    if (typeof raw.error === "string" && raw.error.trim()) return raw.error.trim();
    if (typeof raw.code === "string" && raw.code.trim()) return raw.code.trim();
    try {
      return JSON.stringify(raw);
    } catch (_e) {
      return "Something went wrong. Please try again";
    }
  }
  return String(raw);
};

const emitStoreAuthRequired = (detail) => {
  try {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("store:auth-required", { detail: detail ?? {} })
    );
  } catch (_e) {
    // ignore
  }
};

const persistStoreTokenFromResponse = (response) => {
  const url = response?.config?.url || "";
  if (!(typeof url === "string" && url.startsWith("/store"))) return;

  const body = response?.data;
  const token =
    extractTokenFromPayload(body) ||
    extractTokenFromPayload(body?.data) ||
    extractTokenFromPayload(body?.data?.data);

  if (token) {
    localStorage.setItem("storeAccessToken", token);
  }
};

const isStoreApiUrl = (url) =>
  typeof url === "string" && url.startsWith("/store");

const hasStoreLeadAttributionData = () =>
  Boolean(
    localStorage.getItem("storeLeadMobile") ||
      localStorage.getItem("storeLeadSource") ||
      localStorage.getItem("storeReferralCode")
  );

axiosInstance.interceptors.request.use(
  (config) => {
    const url = config.url || "";
    config.headers = config.headers || {};

    const clientSessionId = getClientSessionId();
    if (clientSessionId) {
      config.headers["X-Client-Session-Id"] = clientSessionId;
    } else {
      delete config.headers["X-Client-Session-Id"];
    }

    // For new Store APIs under /api/store..., use dedicated store token.
    if (typeof url === "string" && url.startsWith("/store")) {
      const storeToken = localStorage.getItem("storeAccessToken");
      const referralCode = localStorage.getItem("storeReferralCode");
      const leadSource = localStorage.getItem("storeLeadSource");
      const leadSourceUrl = localStorage.getItem("storeLeadSourceUrl");
      const leadSourceMeta = localStorage.getItem("storeLeadSourceMeta");
      const leadMobile = localStorage.getItem("storeLeadMobile");
      if (storeToken) config.headers.Authorization = `Bearer ${storeToken}`;
      else delete config.headers.Authorization;

      // Lead cookie + attribution headers on all store calls when present so
      // browsing activities resolve to the lead buyer-user, not the temp guest.
      config.withCredentials =
        isStoreApiUrl(url) &&
        (hasStoreLeadAttributionData() || !storeToken);

      if (isStoreApiUrl(url) && hasStoreLeadAttributionData()) {
        if (referralCode) config.headers["X-Store-Referral-Code"] = referralCode;
        if (leadSource) config.headers["X-Store-Lead-Source"] = leadSource;
        if (leadSourceUrl) config.headers["X-Store-Lead-Source-Url"] = leadSourceUrl;
        if (leadSourceMeta) config.headers["X-Store-Lead-Source-Meta"] = leadSourceMeta;
        if (leadMobile) config.headers["X-Store-Lead-Mobile"] = leadMobile;
      } else {
        delete config.headers["X-Store-Referral-Code"];
        delete config.headers["X-Store-Lead-Source"];
        delete config.headers["X-Store-Lead-Source-Url"];
        delete config.headers["X-Store-Lead-Source-Meta"];
        delete config.headers["X-Store-Lead-Mobile"];
      }
    } else {
      const token = localStorage.getItem("accessToken");
      if (token) config.headers.Authorization = `Bearer ${token}`;
      else delete config.headers.Authorization;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    persistStoreTokenFromResponse(response);

    if (response.is_guest_user) setUserAsGuest();

    if (response.headers["content-disposition"]) {
      return {
        data: response.data,
        "content-disposition": response.headers["content-disposition"],
      };
    }

    return response.data;
  },
  (axiosError) => {
    const response = axiosError?.response;

    // Handle network/timeouts/CORS/cancelled requests where no HTTP response exists.
    if (!response) {
      const isCanceled =
        axiosError?.code === "ERR_CANCELED" ||
        /canceled/i.test(String(axiosError?.message || ""));
      const errorMessage = isCanceled
        ? "Request was canceled. Please try again."
        : "Unable to reach server. Please check your connection and try again.";

      return Promise.reject({
        error: errorMessage,
        property: null,
        status: null,
        isNetworkError: !isCanceled,
        isCanceled,
      });
    }

    let error = null;
    let property = null;
    let errorData = {};

    if (response && response.data) {
      error = response.data.errors || response.data.error || null;
      property = response.data.property || null;

      // Preserve all other fields from response.data (like ids, failed_ids, etc.)
      Object.keys(response.data).forEach((key) => {
        if (key !== "errors" && key !== "error" && key !== "property") {
          errorData[key] = response.data[key];
        }
      });
    }

    const url = response?.config?.url || "";
    const isStoreApiRequest = typeof url === "string" && url.startsWith("/store");
    const isStoreAuthEndpoint =
      typeof url === "string" &&
      (url.startsWith("/store/verify_otp") ||
        url.startsWith("/store/register") ||
        url.startsWith("/store/buyer_users/register") ||
        url.startsWith("/store/dealer_users/register") ||
        url.startsWith("/store/users/login") ||
        url.startsWith("/store/users/admin_login") ||
        url.startsWith("/store/users/generate_otp") ||
        url.startsWith("/store/dealer_users/generate_otp") ||
        url.startsWith("/store/session"));

    if (response.status == 406) {
      error = response.data || response.status;
      error.status = response.status;
    } else if (response.status == 403) {
      error =
        response?.data?.message ||
        response?.data?.error ||
        "You do not have permission to access this resource.";
    } else if (response.status == 400) {
      error = response.data.errors || response.data.error;
      property = response.data.property;
    } else if (response.status == 401) {
      // Store frontend: prefer auth modal over hard redirect + object error rendering.
      // But do NOT override auth endpoints (e.g. invalid OTP should show backend message).
      if (isStoreApiRequest && !isStoreAuthEndpoint) {
        emitStoreAuthRequired({ url, status: 401, data: response.data });
        error = "Please log in to continue";
      }
    } else {
      if (!error) error = "Something went wrong. Please try again";
    }

    error = normalizeErrorMessage(error) || "Something went wrong. Please try again";

    if (error === "Unauthorized") {
      setUserAsGuest();
      const skipRedirect =
        [
          "/products",
          "/materials_price?type=boards&token=925b4b62202d",
        ].includes(url) ||
        (typeof url === "string" && url.startsWith("/store/products"));

      if (!skipRedirect && !isStoreApiRequest) window.location.href = "/login_with_otp";
      error = "Please log in to continue";
    }

    return Promise.reject({ error, property, status: response.status, ...errorData });
  }
);

export default axiosInstance;
